import React, { FC, useCallback, useEffect, useState } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import { useLazyGetBeneficiariesQuery } from 'api/beneficiary';
import { FLOW_PERMISSION_PROPERTIES } from 'constants/accessConstants';
import { ADD_WHITE, IMAGE_PLACEHOLDER, PLUS_CIRCLE, SEARCH_MD } from 'constants/icons';
import { defaultFn, PaymentType } from 'constants/index';
import { StatusType } from 'constants/kyb';
import { WHITE_LOADER } from 'constants/lottie/loading_whilte';
import { NO_BENEFIARIES } from 'constants/lottie/no_beneficiaries';
import mixpanelEvents from 'constants/mixpanel';
import { BUTTON_SIZE_TYPES, BUTTON_TYPES, Text } from 'destiny';
import { debounce } from 'hooks';
import { useAppSelector } from 'hooks/toolkit';
import useConfiguration, { CONFIG_DEFINITIONS } from 'hooks/useConfiguration';
import useUserAccessFromPermission from 'hooks/useUserAccessFromPermission';
import AddRecipient from 'modules/beneficiary/addRecipient/AddRecipient';
import SelectRecipientAccountSkeleton from 'modules/single-transfer/thirdPartyPayouts/steps/skeleton/SelectRecipientAccountSkeleton';
import Image from 'next/image';
import { RootState } from 'store';
import { BeneficiaryType } from 'types/beneficiary';
import { Map } from 'types/kyb';
import { trackMixpanel } from 'utils/mixpanel';
import Avatar from 'components/avatar/Avatar';
import { ErrorCardTypes } from 'components/banners/types';
import ZeroListData from 'components/banners/ZeroListData';
import Input from 'components/input/Input';
import { SkeletonTypes } from 'components/skeletons/types';
import CommonWrapper from 'components/wrappers/CommonWrapper';

interface SelectRecipientAccountProps {
  className?: string;
  onRecipientAccountSelect: (args: any) => void;
}

const SelectRecipientAccount: FC<SelectRecipientAccountProps> = ({
  className,
  onRecipientAccountSelect = defaultFn,
}) => {
  const [search, setSearch] = useState<string>('');
  const [showAddRecipient, setShowAddRecipient] = useState<boolean>(false);
  const { checkConfigEnabled } = useConfiguration();

  const [getBeneficiaries, { data: beneficiaries = [], isFetching, isError }] = useLazyGetBeneficiariesQuery({});

  const { networkCodeAndNameMap, networkCodeAndIconMap, currencyCodeAndNameMap, currencyCodeAndIconMap } =
    useAppSelector((state: RootState) => state.config);

  const { bankingKybStatus } = useAppSelector((state: RootState) => state.kyb);
  const isBankingPreKyb = bankingKybStatus !== StatusType.SUCCESS;
  const canManageRecipients = useUserAccessFromPermission(
    FLOW_PERMISSION_PROPERTIES.MANAGE_RECEPIENTS.permissionId,
    FLOW_PERMISSION_PROPERTIES.MANAGE_RECEPIENTS.scope
  );
  const canAddRecipient =
    checkConfigEnabled(CONFIG_DEFINITIONS.ADD_RECIPIENTS) && !isBankingPreKyb && canManageRecipients;

  const noRecipients = !isFetching && (!beneficiaries || beneficiaries?.length === 0);

  const handleGetBeneficiaries = (params?: Map) => {
    getBeneficiaries(params ?? {});
  };

  useEffect(() => {
    if (search) getBeneficiariesCallback(search);
    else handleGetBeneficiaries();
  }, [search]);

  const getBeneficiariesCallback = useCallback(
    debounce((search: string, type: string[]) => {
      trackMixpanel(mixpanelEvents.SEARCH_BENEFICIARY, { SearchValue: search });
      handleGetBeneficiaries({ search, type });
    }, 500),
    []
  );

  const handleAddRecipient = () => setShowAddRecipient(true);

  const handleBenefitiarySelect = (beneficiary: BeneficiaryType) => {
    trackMixpanel(mixpanelEvents.TRANSFER_SELECT_BENEFICIARY_CLICK, {
      SendBeneId: beneficiary.id,
    });
    onRecipientAccountSelect(beneficiary);
  };

  return (
    <>
      <div className='tw-w-full tw-flex tw-flex-col'>
        <div className='tw-w-full tw-grow tw-relative tw-mb-2'>
          <div className='tw-h-full tw-left-6 tw-absolute tw-z-50 tw-flex tw-items-center'>
            <Image src={SEARCH_MD} alt='search' width={20} height={20} priority />
          </div>
          <Input
            id='search'
            value={search}
            onChange={(evt) => setSearch(evt?.target?.value)}
            className='f-16-300 tw-border-none tw-bg-BASE_PRIMARY tw-text-TEXT_PRIMARY !tw-h-[52px] !tw-rounded-[5px] tw-pl-[60px] !tw-pr-6 !tw-py-4'
            placeHolder='Search by name, account, address..'
          />
          {isFetching && search && (
            <div className='tw-h-full tw-right-6 tw-top-0 tw-absolute tw-z-50 tw-flex tw-items-center'>
              <Player src={JSON.parse(WHITE_LOADER)} className='lottie-player' loop autoplay />
            </div>
          )}
        </div>
      </div>

      <div className='tw-bg-white tw-rounded-[10px] tw-border tw-DIVIDER_GRAY tw-shadow-menu'>
        {canAddRecipient && !noRecipients && (
          <div
            role='presentation'
            onClick={handleAddRecipient}
            className='tw-flex tw-items-center tw-px-6 tw-py-4 tw-border-b tw-DIVIDER_GRAY tw-cursor-pointer'
          >
            <Image src={PLUS_CIRCLE} width={24} height={24} className='tw-mr-3' alt='add' priority />
            <Text textClass='f-14-400 tw-text-TEXT_PRIMARY'>Add a new recipient</Text>
          </div>
        )}

        <CommonWrapper
          isLoading={isFetching}
          skeletonType={SkeletonTypes.CUSTOM}
          skeleton={<SelectRecipientAccountSkeleton />}
          isError={isError}
          errorCardType={ErrorCardTypes.API_FAIL}
          refetchFunnction={handleGetBeneficiaries}
          isNoData={!isFetching && noRecipients}
          noDataBanner={
            <ZeroListData
              handleClick={handleAddRecipient}
              title='No Recipients'
              subtitle='Add a recipient and start transacting with Zamp'
              preKybSubtitle=''
              buttonText='Add Recipient'
              lottieJson={NO_BENEFIARIES}
              lottieDimensions={{ width: '176px', height: '106px' }}
              destinyButtonProps={{
                buttonProps: {
                  btnType: BUTTON_TYPES.PRIMARY,
                  size: BUTTON_SIZE_TYPES.SMALL,
                  onClick: handleAddRecipient,
                  wrapperClass: 'tw-min-w-[172px]',
                },
                customLeadingIcon: <Image src={ADD_WHITE} alt='add' width={20} height={20} />,
                showLeadingIcon: true,
              }}
              showButton={canAddRecipient}
              wrapperClassName='tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[412px]'
              contentClassName='tw-w-[270px] tw-gap-6'
              titleClassName='f-14-400 tw-text-TEXT_GRAY_4'
              subtitleClassName='f-14-300 tw-text-TEXT_GRAY_4 tw-text-center'
              id='SINGLE_TRANSFER_RECEPIENTS'
              productKybStatus={bankingKybStatus}
            />
          }
        >
          <div className={`tw-w-full tw-max-h-[354px] tw-overflow-y-auto ${className}`}>
            {beneficiaries?.map((beneficiary, index: number) => (
              <div
                key={`${beneficiary.id}_${index}`}
                role='presentation'
                className='tw-px-6 tw-py-3 tw-bg-white hover:tw-bg-BASE_SECONDARY tw-rounded-[10px] tw-flex tw-items-center tw-justify-between tw-cursor-pointer'
                onClick={() => handleBenefitiarySelect(beneficiary)}
              >
                <div className='tw-flex tw-items-center tw-w-3/5'>
                  <Avatar
                    name={beneficiary.beneficiary_name}
                    styleClassName='tw-w-6 tw-h-6 f-10-400 '
                    className='tw-mr-3 tw-min-w-[24px]'
                  />
                  <div className='f-14-400 tw-truncate tw-w-[calc(100%-60px)]'>{beneficiary.beneficiary_name}</div>
                </div>
                <div className='tw-flex tw-items-center tw-w-2/5'>
                  <Image
                    src={
                      beneficiary.payout_method === PaymentType.FIAT
                        ? currencyCodeAndIconMap[beneficiary.destination_currency] || IMAGE_PLACEHOLDER
                        : networkCodeAndIconMap[beneficiary.routing_code_value_1] || IMAGE_PLACEHOLDER
                    }
                    alt='Logo'
                    draggable='false'
                    width={24}
                    height={24}
                  />
                  <div className='tw-flex tw-flex-col tw-ml-3 tw-w-[calc(100%-36px)]'>
                    <div className='tw-text-TEXT_PRIMARY f-14-400 tw-truncate tw-mb-0.5'>
                      {beneficiary.payout_method === PaymentType.FIAT
                        ? `${currencyCodeAndNameMap[beneficiary.destination_currency]} account`
                        : networkCodeAndNameMap[beneficiary.routing_code_value_1]}
                    </div>
                    <div className='tw-text-TEXT_SECONDARY f-12-300 tw-truncate'>
                      {beneficiary.beneficiary_account_number}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CommonWrapper>
      </div>
      {showAddRecipient && <AddRecipient isOpen={showAddRecipient} onClose={setShowAddRecipient} />}
    </>
  );
};

export default SelectRecipientAccount;
